/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {_BaseNodeRenderer, Adapter, AdapterBuilder, BaseComponent, ItemToRender, MenuPosition, NodeRendererProps, TS_Checkbox} from "@intuitionrobotics/thunderstorm/frontend";
import {MenuAndButton} from "@intuitionrobotics/thunderstorm/app-frontend/modules/menu/MenuAndButton";
import * as React from "react";
import {CSSProperties, ReactNode} from "react";
import * as emotion from "emotion";
import {COLORS} from "@res/colors";
import {generateHex} from "@intuitionrobotics/ts-common";

export type RendererString = {
    isChecked: (option: CheckboxOptions) => boolean,
    toDisplay: CheckboxOptions,
    onClick?: any
    count: number
    checkboxRender?: (checked: boolean, disabled: boolean) => ReactNode,
    rtl?: boolean,
    outerNode?: (checked: boolean, disabled: boolean) => string,
    containerClass?: (checked: boolean, disabled: boolean) => string,
    renderLabel?: (checked: boolean, disabled: boolean) => CSSProperties
};
const Style_Checkbox = {
    width: 13,
    height: 13,
    borderWidth: 1,
    borderColor: COLORS.grays_2,
    borderStyle: "solid",
    borderRadius: 2
};

type HeaderRm = {
    node: _BaseNodeRenderer<RendererString>
};

const menuClass = emotion.css`
	font-size: 14px;
	font-weight: 300;
	padding: 8px;
	display: flex;
	cursor:pointer;
	width: -webkit-fill-available;
	&:hover {
	background-color:${COLORS.primary_blue_10}
	}
`;

const innerNode = (checked: boolean, disabled: boolean) => {
    return (
        <div
            className={"flex_center"}
            style={Style_Checkbox}
        >
            {checked && <div className={"flex_center"}
                             style={{
                                 width: 9,
                                 height: 9,
                                 backgroundColor: COLORS.primary_blue,
                                 borderRadius: 2
                             }}>
            </div>}
        </div>);
};

export interface OnChecked {
    __onChecked: () => void;
}

export class CheckboxRender
    extends BaseComponent<NodeRendererProps<RendererString>, {}> implements OnChecked {

    constructor(props: NodeRendererProps<RendererString>) {
        super(props);
    }

    __onChecked() {
        this.forceUpdate();
    }

    render() {
        const checked = this.props.item.isChecked(this.props.item.toDisplay);
        return <div
            style={{alignSelf: "flex-start", color: COLORS.neutral_text_dark, fontSize: 14, padding: "8px"}}
            className={menuClass}
            onClick={() => this.props.item.onClick(this.props.item.toDisplay.name, this.props.item.toDisplay._id)}
        >
            <TS_Checkbox
                innerNode={this.props.item.checkboxRender || innerNode}
                value={this.props.item.toDisplay.name}
                checked={checked}
                label={<div style={this.props.item.renderLabel && this.props.item.renderLabel(checked, false)}>{this.props.item.toDisplay.name}</div>}
                rtl={this.props.item.rtl}
                buttonClass={this.props.item.outerNode}
                containerClass={this.props.item.containerClass}
                // onCheck={(filter) => this.props.item.onClick(filter)}
            />
            {this.props.item.count > 0 && <div style={{marginLeft: "auto", color: COLORS.bluish}}>{this.props.item.count}</div>}
        </div>;
    }
}

// const rendererMap: HeaderRm = {
// 	node: CheckboxRender
// };

export enum CheckboxOptionsType {
    Alert = "Notification",
    Physician = "Physician",
    CareManager = "Care manager",
    Organizations = "Organizations",
    Patients = "Customers",
    Status = "Status",
    SubGroups = "Sub groups",
    AppRoles = "App roles",
}

export type CheckboxOptions = {
    _id?: string,
    name: string,
    type: CheckboxOptionsType,
    source: "Alerts" | "Customers" | string
    count: number | null
}

type Props = {
    isChecked: (option: CheckboxOptions) => boolean,
    options: CheckboxOptions[],
    onClick: (filter: string, _id?: string) => void,
    iconOpen: ReactNode,
    iconClose: ReactNode
    checkboxRender?: (checked: boolean, disabled: boolean) => ReactNode,
    rtl?: boolean
    outerNode?: (checked: boolean, disabled: boolean) => string,
    containerClass?: (checked: boolean, disabled: boolean) => string,
    renderLabel?: (checked: boolean, disabled: boolean) => CSSProperties
}

export class Checkbox
    extends BaseComponent<Props, {}> {

    private rendererMap: HeaderRm = {
        node: CheckboxRender
    };

    buildElements = (): ItemToRender<HeaderRm>[] =>
        this.props.options.map(_option => ({
            item: {
                isChecked: this.props.isChecked,
                toDisplay: _option,
                onClick: this.props.onClick,
                count: _option.count as number,
                checkboxRender: this.props.checkboxRender,
                rtl: this.props.rtl,
                outerNode: this.props.outerNode,
                containerClass: this.props.containerClass,
                renderLabel: this.props.renderLabel
            },
            type: "node"
        }));

    resolvePosition = (button: HTMLImageElement): MenuPosition => {
        const pos = button.getBoundingClientRect();
        return {top: pos.top + button.offsetHeight, left: pos.x + 15};
    };

    render() {
        const adapter: Adapter = AdapterBuilder()
            .list()
            .multiRender(this.rendererMap)
            .setData(this.buildElements())
            .build();

        return <div className={`ll_h_c`}>
            <MenuAndButton
                id={generateHex(6)}
                iconClosed={this.props.iconClose}
                iconOpen={this.props.iconOpen}
                adapter={adapter}
                resolvePosition={this.resolvePosition}
                css={{overflowY: "auto", width: "325px"}}
            />
        </div>;
    }
}
